import { useCallback, useEffect, useRef, useState } from "react";
import { LAST_ID_KEY, POLL_MS, SOS_EVENT, mergeSos } from "./sosAlertRules";
import { getNewSos } from "./sosAlertService";

/**
 * Checks the airline's SOS while a user is logged in: the first check only learns the latest SOS id (kept per tab in
 * sessionStorage), then every POLL_MS it asks for the SOS raised after it. New SOS are added to `alerts`, passed to
 * onNew(items) (sound, desktop notification) and announced with the SOS_EVENT window event. A failed check is
 * silent: the next one catches up, nothing is lost. dismiss() empties the list (popup closed).
 */
export default function useSosWatch({ onNew, interval = POLL_MS, check = getNewSos } = {}) {
  const [alerts, setAlerts] = useState([]);
  const onNewRef = useRef(onNew);
  onNewRef.current = onNew;

  useEffect(() => {
    let stopped = false;
    let busy = false;
    const tick = async () => {
      const airline = sessionStorage.getItem("session_airlineCode");
      if (!airline || busy) return;
      busy = true;
      try {
        const seen = Number(sessionStorage.getItem(LAST_ID_KEY)) || 0;
        const result = await check(airline, seen);
        if (stopped || !result) return;
        if (result.lastId > seen) sessionStorage.setItem(LAST_ID_KEY, String(result.lastId));
        const items = seen > 0 ? result.items ?? [] : [];
        if (items.length) {
          setAlerts((current) => mergeSos(current, items));
          if (onNewRef.current) onNewRef.current(items);
          window.dispatchEvent(new CustomEvent(SOS_EVENT, { detail: items }));
        }
      } catch (err) {
        // silent: the next check retries
      } finally {
        busy = false;
      }
    };
    tick();
    const timer = setInterval(tick, interval);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [check, interval]);

  const dismiss = useCallback(() => setAlerts([]), []);
  return { alerts, dismiss };
}
